import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HeroImage } from '../elements/HeroImage';
import { ReservationHistoryContainer } from '../elements/ReservationHistoryContainer';
import {
  ServiceTypeSelector,
  type ServiceTypeOption,
} from '../elements/ServiceTypeSelector';
import { ROUTE_PATH } from '@/constants/routes';
import { SPACE_TYPE, type SpaceType } from '@/constants/space';
import { useDialog } from '@/hooks/useDialog';
import { useUser } from '@/hooks/useUser';

export const ReservationHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialServiceType = searchParams.get('type') || '';
  const [selectedServiceType, setSelectedServiceType] =
    useState(initialServiceType);

  // サービスタイプ変更時にURLも更新
  const handleServiceTypeChange = (value: string) => {
    setSelectedServiceType(value);
    setSearchParams({ type: value });
  };

  // ユーザー情報を取得
  const { isAnaLoungeMember } = useUser();

  // ana_lounge会員の場合はboothオプションを無効化
  const serviceTypeOptions: ServiceTypeOption[] = [
    {
      value: SPACE_TYPE.SHOWER_ROOM,
      labelKey: 'reservationHistory.serviceType.shower',
    },
    {
      value: SPACE_TYPE.PRIVATE_ROOM,
      labelKey: 'reservationHistory.serviceType.booth',
      disabled: isAnaLoungeMember,
      disabledLabel: isAnaLoungeMember
        ? t('reservation.serviceType.boothRestriction')
        : undefined,
    },
  ];

  const {
    dialogRef: noReservationsDialogRef,
    open: openNoReservationsDialog,
    close: closeNoReservationsDialog,
  } = useDialog();

  // selectedServiceTypeをそのままspaceTypeとして使用
  const spaceType = (selectedServiceType as SpaceType) || undefined;

  const handleNoReservations = useCallback(() => {
    openNoReservationsDialog();
  }, [openNoReservationsDialog]);

  const handleBackToReservation = () => {
    closeNoReservationsDialog();
    navigate(ROUTE_PATH.RESERVATION);
  };

  return (
    <div className="page p-auth page_c1">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                {/* ヒーロー */}
                <HeroImage src="/Common_Title.jpg" alt="LOUNGE RESERVATION" />

                {/* エリア群（グリッド） */}
                <div className="l-grid">
                  {/* ボックス1：サービス */}
                  <section className="l-card reservation-card">
                    <ServiceTypeSelector
                      selectedValue={selectedServiceType}
                      onChange={handleServiceTypeChange}
                      options={serviceTypeOptions}
                      className=""
                      leadTextKey="reservationHistory.selectService"
                    />
                  </section>

                  {selectedServiceType && (
                    <section className="l-card">
                      <div className="l-card__body">
                        <div className="reservation-card__history">
                          <div className="reservation-card__history-title">
                            {t('reservationHistory.listTitle')}
                          </div>
                          <div className="reservation-card__info reservation-card__info--timezone">
                            {t('reservation.boarding.timezoneNote')}
                          </div>

                          <ReservationHistoryContainer
                            spaceType={spaceType}
                            selectedServiceType={selectedServiceType}
                            onNoReservations={handleNoReservations}
                          />
                        </div>

                        {/* アクションエリア */}
                        <div className="reservation-card__actions">
                          <div className="reservation-card__actions-item">
                            <button
                              type="button"
                              className="reservation-card__action-link reservation-card__action-link--back"
                              onClick={() => navigate(ROUTE_PATH.RESERVATION)}
                            >
                              {t('reservationHistory.backToReservation')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* 予約なしダイアログ */}
      <dialog
        className="dialog dialog__confirm-email"
        ref={noReservationsDialogRef}
      >
        <div className="dialog__line">
          <div className="dialog__area">
            <button
              className="dialog__btn--close js-close-dialog"
              type="button"
              onClick={closeNoReservationsDialog}
            >
              ×
            </button>
            <div className="dialog__text">
              <p className="dialog__text-line">
                {t('reservationHistory.noReservations')}
              </p>
            </div>
            <div className="dialog__actions">
              <button
                className="dialog__btn dialog__btn--return js-bt-dialog"
                type="button"
                onClick={handleBackToReservation}
              >
                {t('reservationHistory.backToReservation')}
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};
